import * as React from 'react';
import {useEffect, useState} from 'react';
import {graphql, PageProps} from 'gatsby';
import FadedScreen from 'layout/FadedScreen';
import WorkCategories from 'components/WorkCategories';
import {Category} from 'components/WorkCategories/Desktop';

export type RawCategories = typeof initialCategories;
type Props = PageProps & {
  data: {
    allMarkdownRemark: {
      edges: Array<{
        node: {
          frontmatter: {
            category: keyof RawCategories;
          };
        };
      }>;
    };
    markdownRemark: {
      frontmatter: {
        categoryImages: {
          photography: string;
          textile: string;
          writtenWord: string;
          sound: string;
          video: string;
          sculpture: string;
          mixedMedia: string;
          drawing: string;
          painting: string;
        };
        seoDescription: string;
        seoKeywords: string;
        title: string;
      };
    };
  };
};

const initialCategories = {
  photography: {count: 0, imageSrc: ''},
  textile: {count: 0, imageSrc: ''},
  writtenWord: {count: 0, imageSrc: ''},
  sound: {count: 0, imageSrc: ''},
  video: {count: 0, imageSrc: ''},
  sculpture: {count: 0, imageSrc: ''},
  mixedMedia: {count: 0, imageSrc: ''},
  drawing: {count: 0, imageSrc: ''},
  painting: {count: 0, imageSrc: ''},
};

export default function IndexPage({data}: Props) {
  const {frontmatter} = data.markdownRemark;
  const works = data.allMarkdownRemark.edges;
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const updatedCategories: RawCategories = {
      photography: {count: 0, imageSrc: ''},
      textile: {count: 0, imageSrc: ''},
      writtenWord: {count: 0, imageSrc: ''},
      sound: {count: 0, imageSrc: ''},
      video: {count: 0, imageSrc: ''},
      sculpture: {count: 0, imageSrc: ''},
      mixedMedia: {count: 0, imageSrc: ''},
      drawing: {count: 0, imageSrc: ''},
      painting: {count: 0, imageSrc: ''},
    };

    works.forEach(
      ({
        node: {
          frontmatter: {category},
        },
      }) => {
        updatedCategories[category].count++;
        updatedCategories[category].imageSrc = frontmatter.categoryImages[category];
      }
    );

    const cleanCategories: Category[] = [];
    Object.entries(updatedCategories).forEach(([category, {count, imageSrc}]) => {
      if (count > 0 && imageSrc) {
        // @ts-ignore
        cleanCategories.push({anchor: kebabedWords(category), category: capitalisedWords(category), count, imageSrc});
      }
    });

    setCategories(cleanCategories);
  }, []);

  function capitalisedWords(name: string): string {
    const words = name.match(/[A-Za-z][a-z]*/g) || [];
    return words.map(word => word.charAt(0).toUpperCase() + word.substring(1)).join(' ');
  }

  function kebabedWords(str: string): string {
    return str
      .split('')
      .map((letter, idx) => {
        return letter.toUpperCase() === letter ? `${idx !== 0 ? '-' : ''}${letter.toLowerCase()}` : letter;
      })
      .join('');
  }

  return (
    <FadedScreen>
      <WorkCategories categories={categories} />
    </FadedScreen>
  );
}

export const query = graphql`
  query IndexPage($id: String!) {
    allMarkdownRemark(filter: {frontmatter: {templateKey: {eq: "work-page"}}}) {
      edges {
        node {
          frontmatter {
            category
          }
        }
      }
    }
    markdownRemark(id: {eq: $id}) {
      frontmatter {
        categoryImages {
          photography
          textile
          writtenWord
          sound
          video
          sculpture
          mixedMedia
          drawing
          painting
        }
        seoDescription
        seoKeywords
        title
      }
    }
  }
`;
